import React from 'react';
import { Mountain, Loader2, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

// --- 1. LAYOUT GLOBAL (Fond + Carte + Logo) ---
interface AuthLayoutProps {
    children: React.ReactNode;
    title: string;
    subtitle: string;
    footer?: React.ReactNode;
}

export function AuthLayout({ children, title, subtitle, footer }: AuthLayoutProps) {
    const { t } = useTranslation();

    return (
        <div className="min-h-screen w-full flex items-center justify-center bg-slate-50 relative overflow-hidden">

            {/* Décoration d'arrière-plan */}
            <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-emerald-400/20 rounded-full blur-[100px] pointer-events-none" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-[100px] pointer-events-none" />

            {/* Carte Centrale */}
            <div className="w-full max-w-md px-4 sm:px-6 py-6 relative z-10 animate-in fade-in zoom-in-95 duration-300">
                <div className="bg-white/80 backdrop-blur-xl border border-white/50 shadow-2xl rounded-3xl p-5 sm:p-8 space-y-8">

                    {/* En-tête Logo */}
                    <div className="text-center space-y-2">
                        <div className="w-16 h-16 bg-emerald-600 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-emerald-200 rotate-3 mb-4">
                            <Mountain className="text-white" size={32} />
                        </div>
                        <h1 className="text-3xl font-black text-slate-900 tracking-tight">
                            TrailBets
                        </h1>
                        <p className="text-slate-500 font-medium">
                            {subtitle}
                        </p>
                    </div>

                    {/* Titre de la section (ex: Connexion / Inscription) */}
                    <h2 className="text-xl font-bold text-center text-slate-900 sr-only">{title}</h2>

                    {children}

                    {/* Footer (Liens) */}
                    {footer && (
                        <div className="text-center pt-2">
                            {footer}
                        </div>
                    )}
                </div>

                <p className="text-center text-slate-300 text-xs mt-8">
                    {t('footer.copyright')}
                </p>
            </div>
        </div>
    );
}

// --- 2. INPUT STANDARDISÉ ---
interface AuthInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label: string;
    icon: LucideIcon;
}

export function AuthInput({ label, icon: Icon, className, ...props }: AuthInputProps) {
    return (
        <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700 ml-1">
                {label}
            </label>
            <div className="relative group overflow-hidden rounded-2xl">
                <Icon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-500 transition-colors" size={20} />
                <input
                    className={cn(
                        "w-full h-14 pl-12 pr-4 bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all font-medium text-slate-900 placeholder:text-slate-400",
                        className
                    )}
                    {...props}
                />
            </div>
        </div>
    );
}

// --- 3. BOUTON ACTION ---
interface AuthButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    isLoading?: boolean;
    children: React.ReactNode;
}

export function AuthButton({ isLoading, children, className, ...props }: AuthButtonProps) {
    return (
        <button
            disabled={isLoading || props.disabled}
            className={cn(
                "w-full h-14 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg shadow-emerald-500/20",
                isLoading
                    ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                    : "bg-emerald-600 text-white hover:bg-emerald-700 hover:shadow-emerald-500/30",
                className
            )}
            {...props}
        >
            {isLoading ? <Loader2 className="animate-spin" /> : children}
        </button>
    );
}
